import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { AlertCircle, Banknote, CalendarX, CreditCard, Loader2, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@cms/ui/components/table";
import { PageHeader } from "@cms/ui/components/page-header";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { formatPence, formatTimestamp, parseTimestamp } from "@cms/portal-lib/format";
import {
  billingStatusBadgeVariant,
  billingStatusLabel,
  invoiceStatusBadgeVariant,
  openInvoiceHtml,
} from "@cms/portal-lib/billing";
import { adminInvoiceHtmlPath, fetchBillingSummary } from "../lib/billing";
import type { AdminOutletContext } from "../lib/outlet-context";

function StatTile({ title, icon: Icon, value, hint }: {
  title: string;
  icon: LucideIcon;
  value: React.ReactNode;
  hint: string;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

function daysOverdue(since: string | null): string {
  const date = parseTimestamp(since);
  if (!date) return "—";
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  return days === 1 ? "1 day" : `${days} days`;
}

export function BillingPage() {
  const { api } = useOutletContext<AdminOutletContext>();
  const query = useApiQuery(() => (api ? fetchBillingSummary(api) : null), [api]);
  const summary = query.data;

  const pastDueCount = (summary?.counts.past_due ?? 0) + (summary?.counts.suspended ?? 0);

  return (
    <div>
      <PageHeader
        title="Billing"
        description="Recurring revenue, payment health and recent invoices across all clients."
      />

      {query.error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't load billing</AlertTitle>
          <AlertDescription>{query.error}</AlertDescription>
        </Alert>
      )}

      {!summary && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {summary && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              title="MRR"
              icon={Banknote}
              value={formatPence(summary.mrr_pence)}
              hint="Monthly recurring (ex VAT), after locks & adjustments"
            />
            <StatTile
              title="Active subscriptions"
              icon={CreditCard}
              value={summary.counts.active ?? 0}
              hint="Clients with a paying subscription"
            />
            <StatTile
              title="Past due"
              icon={TriangleAlert}
              value={pastDueCount}
              hint="Overdue, including suspended accounts"
            />
            <StatTile
              title="Cancelled"
              icon={CalendarX}
              value={summary.counts.cancelled ?? 0}
              hint="Cancelled subscriptions"
            />
          </div>

          {summary.past_due.length > 0 && (
            <Card className="mt-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Past due</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Overdue</TableHead>
                      <TableHead>Last failed amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.past_due.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Link className="font-medium text-primary hover:underline" to={`/clients/${row.id}/billing`}>
                            {row.business_name || row.id}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant={billingStatusBadgeVariant(row.billing_status)}>
                            {billingStatusLabel(row.billing_status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{daysOverdue(row.billing_past_due_since)}</TableCell>
                        <TableCell>
                          {row.failed_total_pence != null ? formatPence(row.failed_total_pence) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card className="mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {summary.recent_invoices.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground">
                  No invoices yet — they appear here as GoCardless payments come in.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issued</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.recent_invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <button
                            type="button"
                            className="font-medium text-primary hover:underline"
                            onClick={() => {
                              if (api) {
                                openInvoiceHtml(api, adminInvoiceHtmlPath(invoice.tenant_id, invoice.id))
                                  .catch(() => { /* surfaced by the browser tab failing to open */ });
                              }
                            }}
                          >
                            {invoice.number}
                          </button>
                        </TableCell>
                        <TableCell>
                          <Link className="hover:underline" to={`/clients/${invoice.tenant_id}/billing`}>
                            {invoice.business_name || invoice.tenant_id}
                          </Link>
                        </TableCell>
                        <TableCell>{formatPence(invoice.total_pence)}</TableCell>
                        <TableCell>
                          <Badge variant={invoiceStatusBadgeVariant(invoice.status)}>{invoice.status}</Badge>
                        </TableCell>
                        <TableCell>{formatTimestamp(invoice.issued_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
